import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Clock, ChevronLeft, ChevronRight, Flag } from 'lucide-react';

export default function ExamInterface() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionStatuses, setQuestionStatuses] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timerRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    startExam();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (timeLeft === 0 && exam) {
      handleAutoSubmit();
    }
  }, [timeLeft]);

  const startExam = async () => {
    try {
      const response = await axios.post(`${API}/student/exam/${examId}/start`);
      setExam(response.data.exam);
      setAttempt(response.data.attempt);
      
      // Load existing answers
      const existingAnswers = response.data.attempt.answers || {};
      const loadedAnswers = {};
      const loadedStatuses = {};
      
      Object.keys(existingAnswers).forEach(qId => {
        loadedAnswers[qId] = existingAnswers[qId].answer;
        loadedStatuses[qId] = existingAnswers[qId].status;
      });
      
      setAnswers(loadedAnswers);
      setQuestionStatuses(loadedStatuses);
      
      // Calculate time left
      const startedAt = new Date(response.data.attempt.started_at);
      const durationMs = response.data.exam.duration_minutes * 60 * 1000;
      const elapsed = Date.now() - startedAt.getTime();
      const remaining = Math.max(0, Math.floor((durationMs - elapsed) / 1000));
      setTimeLeft(remaining);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Enter fullscreen
      document.documentElement.requestFullscreen().catch(() => {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start exam');
      navigate('/student/dashboard');
    }
  };

  const saveAnswer = async (questionId, answer, status) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.post(`${API}/student/exam/answer`, {
          question_id: questionId,
          answer,
          status
        });
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, 500);
  };

  const handleAnswerChange = (value) => {
    const currentQuestion = exam.questions[currentQuestionIndex];
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    const newStatuses = { 
      ...questionStatuses, 
      [currentQuestion.id]: questionStatuses[currentQuestion.id] === 'marked_for_review' ? 'marked_for_review' : 'answered'
    };
    
    setAnswers(newAnswers);
    setQuestionStatuses(newStatuses);
    saveAnswer(currentQuestion.id, value, newStatuses[currentQuestion.id]);
  };

  const handleMarkForReview = () => {
    const currentQuestion = exam.questions[currentQuestionIndex];
    const newStatuses = { ...questionStatuses, [currentQuestion.id]: 'marked_for_review' };
    setQuestionStatuses(newStatuses);
    saveAnswer(currentQuestion.id, answers[currentQuestion.id] || '', 'marked_for_review');
    toast.info('Question marked for review');
  };

  const handleNext = () => {
    if (currentQuestionIndex < exam.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleAutoSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/student/exam/submit`, { attempt_id: attempt.id });
      toast.success('Exam auto-submitted due to time expiry');
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
      navigate('/student/dashboard');
    } catch (error) {
      toast.error('Failed to submit exam');
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/student/exam/submit`, { attempt_id: attempt.id });
      toast.success('Exam submitted successfully!');
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
      navigate('/student/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit exam');
      setIsSubmitting(false);
    }
  };

  if (!exam || !attempt) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const currentQuestion = exam.questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion.id] || '';
  
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionStatus = (questionId) => {
    const status = questionStatuses[questionId];
    if (status === 'answered') return 'question-answered';
    if (status === 'marked_for_review') return 'question-marked';
    return 'question-unanswered';
  };

  const answeredCount = Object.values(questionStatuses).filter(s => s === 'answered').length;
  const markedCount = Object.values(questionStatuses).filter(s => s === 'marked_for_review').length;
  const unansweredCount = exam.questions.length - answeredCount - markedCount;

  return (
    <div className="exam-fullscreen bg-gray-50" data-testid="exam-interface">
      {/* Header with Timer */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="exam-title">{exam.title}</h1>
              <p className="text-sm text-gray-600">Question {currentQuestionIndex + 1} of {exam.questions.length}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 bg-sky-50 px-4 py-2 rounded-lg">
                <Clock className="h-5 w-5 text-sky-600" />
                <span className={`text-lg font-mono font-semibold ${
                  timeLeft < 300 ? 'text-red-600' : 'text-sky-600'
                }`} data-testid="exam-timer">
                  {formatTime(timeLeft)}
                </span>
              </div>
              <Button 
                onClick={() => setShowSubmitDialog(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="exam-submit-btn"
              >
                Submit Exam
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Question Area */}
        <div className="flex-1 overflow-y-auto p-6">
          <Card className="max-w-4xl mx-auto border-sky-100">
            <CardContent className="p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900" data-testid="question-text">
                  {currentQuestion.question_text}
                </h2>
                <div className="text-sm text-gray-500">
                  <span>Marks: {currentQuestion.marks}</span>
                </div>
              </div>

              {/* Answer Input based on question type */}
              {currentQuestion.question_type === 'mcq' && (
                <RadioGroup value={currentAnswer} onValueChange={handleAnswerChange} data-testid="mcq-options">
                  <div className="space-y-3">
                    {currentQuestion.options?.map((option, idx) => (
                      <div key={idx} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <RadioGroupItem value={option} id={`option-${idx}`} data-testid={`option-${idx}`} />
                        <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer text-base">{option}</Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              )}

              {currentQuestion.question_type === 'true_false' && (
                <RadioGroup value={currentAnswer} onValueChange={handleAnswerChange} data-testid="true-false-options">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <RadioGroupItem value="True" id="true" data-testid="option-true" />
                      <Label htmlFor="true" className="flex-1 cursor-pointer text-base">True</Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <RadioGroupItem value="False" id="false" data-testid="option-false" />
                      <Label htmlFor="false" className="flex-1 cursor-pointer text-base">False</Label>
                    </div>
                  </div>
                </RadioGroup>
              )}

              {currentQuestion.question_type === 'short_answer' && (
                <Textarea
                  value={currentAnswer}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                  placeholder="Type your answer here..."
                  className="min-h-[150px] border-sky-200"
                  data-testid="short-answer-input"
                />
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                  variant="outline"
                  data-testid="previous-btn"
                  className="border-sky-200"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={handleMarkForReview}
                  variant="outline"
                  data-testid="mark-review-btn"
                  className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                >
                  <Flag className="h-4 w-4 mr-2" />
                  Mark for Review
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={currentQuestionIndex === exam.questions.length - 1}
                  data-testid="next-btn"
                  className="bg-sky-600 hover:bg-sky-700"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Question Palette */}
        <div className="w-80 bg-white border-l border-gray-200 p-6 overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Question Palette</h3>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-6 h-6 rounded question-answered"></div>
              <span>Answered ({answeredCount})</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-6 h-6 rounded question-unanswered"></div>
              <span>Unanswered ({unansweredCount})</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-6 h-6 rounded question-marked"></div>
              <span>Marked for Review ({markedCount})</span>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-2" data-testid="question-palette">
            {exam.questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentQuestionIndex(idx)}
                className={`w-10 h-10 rounded flex items-center justify-center text-sm font-semibold ${
                  idx === currentQuestionIndex ? 'ring-2 ring-sky-600 ring-offset-2' : ''
                } ${getQuestionStatus(q.id)}`}
                data-testid={`palette-question-${idx + 1}`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Exam?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to submit the exam? You have:
              <ul className="mt-2 space-y-1">
                <li>• {answeredCount} answered questions</li>
                <li>• {markedCount} marked for review</li>
                <li>• {unansweredCount} unanswered questions</li>
              </ul>
              <p className="mt-3 font-semibold">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-submit-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSubmit} 
              disabled={isSubmitting}
              data-testid="confirm-submit-btn"
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Exam'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}